// lib/permissions.ts
// Named permissions and resolution logic for RBAC Level 3.

import type { UserProfile } from "./UserContext";
import type { ActiveStaff } from "./StaffContext";

export const PERMISSIONS = [
  "view_all_conversations",
  "send_messages",
  "assign_conversations",
  "manage_knowledge",
  "manage_templates",
  "manage_reminders",
  "view_dashboard",
  "manage_billing",
  "manage_clinic_settings",
  "manage_bot",
  "view_patients",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ALL_PERMISSIONS = new Set<Permission>(PERMISSIONS);

/**
 * Computes the effective permission set for the current session.
 *
 * Level 1/2 (rbac_enabled = false): all permissions (legacy behaviour).
 * Level 3 (rbac_enabled = true):
 *   - is_clinic_admin → all permissions
 *   - otherwise → permissions from the jsonb field
 */
export function computePermissions(
  user: UserProfile | null,
  activeStaff: ActiveStaff | null
): Set<Permission> {
  if (!user) return new Set();

  // Platform admin: unrestricted
  if (user.role === "admin") return new Set(ALL_PERMISSIONS);

  // RBAC disabled: full clinic access
  if (!user.rbac_enabled) return new Set(ALL_PERMISSIONS);

  // RBAC enabled but no staff selected yet
  if (!activeStaff) return new Set();

  // Clinic admin: all permissions
  if (activeStaff.is_clinic_admin) return new Set(ALL_PERMISSIONS);

  // Regular staff: derive from permissions object
  return new Set<Permission>(
    (Object.entries(activeStaff.permissions) as [Permission, boolean][])
      .filter(([, v]) => v === true)
      .map(([k]) => k)
  );
}
